// src/pages/Staff.js
//
// Staff Management.
//
// - Reads / writes users/{uid}.
// - Staff Settings flags (hideFromTeamMessage, showInDropdowns, receive*Emails)
//   go through ../utils/staffSettingsUtils so this page, the Staff Settings page
//   and the Cloud Functions all agree on field names and defaults.
// - Sub Contractor staff are mirrored into the "contractors" collection.
// - Files upload to Storage at staff/{staffId}/...
// - Every popup renders through createPortal with self-contained inline styles.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { initializeApp, getApps } from "firebase/app";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  addDoc,
} from "firebase/firestore";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { app, db } from "../firebaseConfig";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  STAFF_SETTING_FIELDS,
  applyStaffSettingsToRecord,
  resolveStaffSettings,
} from "../utils/staffSettingsUtils";
import "./Staff.css";

const DEFAULT_STAFF_SKILLS = [
  "All",
  "Supervisor",
  "General Pest Control",
  "Termite",
  "WDI Inspection",
  "Rodent Control",
  "Wildlife",
  "Mosquito",
  "Bed Bug",
  "Cockroach",
  "Ants",
  "Stinging Insects",
  "Fumigation",
  "Lawn & Ornamental",
  "Exclusion",
  "Crawl Space / Moisture",
  "Commercial Accounts",
  "Residential Accounts",
  "Inspection",
  "Sales",
  "Customer Service",
  "Other",
];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
  "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
  "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const ROLE_OPTIONS = [
  ["Admin", "Admin"],
  ["Manager", "Manager"],
  ["Supervisor", "Supervisor"],
  ["Tech", "Tech"],
  ["KondeElectric", "Konde Electric"],
  ["Assistant", "Assistant"],
  ["PiedmontBoard", "Piedmont Board"],
];

const COI_OPTIONS = [
  ["generalLiability", "General Liability Insurance"],
  ["autoInsurance", "Auto Insurance"],
  ["workersComp", "Workers Comp Insurance"],
];

const PAY_METHOD_LABELS = {
  hourly_only: "Hourly Only",
  greater_of_hourly_or_percentage: "Greater of Hourly or Job %",
  percentage_only: "Job % Only",
  hourly_plus_percentage: "Hourly + Job %",
};

const emptyCoi = () => ({
  generalLiability: { selected: false, insurerName: "", policyNumber: "", expirationDate: "" },
  autoInsurance: { selected: false, insurerName: "", policyNumber: "", expirationDate: "" },
  workersComp: { selected: false, insurerName: "", policyNumber: "", expirationDate: "" },
});

const emptyNewStaff = () => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  social: "",
  ein: "",
  dob: "",
  workerType: "Employee",
  companyName: "",
  coi: emptyCoi(),
  startDate: "",
  terminationDate: "",
  role: "Tech",
  address: "",
  city: "",
  state: "",
  zip: "",
  position: "",
  payCalculationMethod: "hourly_only",
  payType: "hourly", // "hourly" | "commission"
  hourlyRate: "",
  commissionRate: "", // percent (10 = 10%)
  salary: "",
  yearlySalary: "",
  hoursPerDay: "8",
  dailyPayRate: "",
  status: "Active",
  password: "",
  skills: [],
  otherSkills: "",
  files: [],
});

const normalizeSkillName = (value = "") => String(value || "").trim();

const uniqueSkills = (skills = []) =>
  Array.from(new Set(skills.map(normalizeSkillName).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );

const HOURS_PER_YEAR = 2080;

const calculateYearlyFromHourly = (hourlyValue) => {
  const hourly = Number(hourlyValue);
  if (!Number.isFinite(hourly) || hourlyValue === "") return "";
  return (hourly * HOURS_PER_YEAR).toFixed(2);
};

const calculateHourlyFromYearly = (yearlyValue) => {
  const yearly = Number(yearlyValue);
  if (!Number.isFinite(yearly) || yearlyValue === "") return "";
  return (yearly / HOURS_PER_YEAR).toFixed(2);
};

const maskSocial = (value = "") => {
  if (!value) return "";
  const clean = String(value).replace(/\D/g, "");
  if (clean.length < 4) return "***-**-****";
  return `***-**-${clean.slice(-4)}`;
};

// Creating an Auth user with the client SDK signs that new user in on the auth
// instance used. A secondary Firebase app keeps the admin who is adding staff
// signed in on the main app.
const getStaffCreatorAuth = () => {
  const name = "staff-creator";
  const existing = getApps().find((a) => a.name === name);
  const secondary = existing || initializeApp(app.options, name);
  return getAuth(secondary);
};

// ---------------------------------------------------------------------------
// Popup shell: portal + fully inline styles (class-based .modal rules in other
// CSS files are unreliable in this app).
// ---------------------------------------------------------------------------
const StaffPopup = ({ children, layer = 0, width = 760 }) => {
  if (typeof document === "undefined") return null;
  const base = layer > 0 ? 1999998 : 999998;

  return createPortal(
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,.55)",
          zIndex: base,
        }}
      />
      <div
        className="staff-modal-box"
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: `min(${width}px, calc(100vw - 24px))`,
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          background: "#ffffff",
          color: "#0f172a",
          padding: 22,
          borderRadius: 12,
          boxShadow: "0 24px 60px rgba(15,23,42,.35)",
          boxSizing: "border-box",
          zIndex: base + 1,
          textAlign: "left",
        }}
      >
        {children}
      </div>
    </>,
    document.body
  );
};

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState(emptyNewStaff);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedStaff, setEditedStaff] = useState({});
  const [showAddStaffPopup, setShowAddStaffPopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [socialRevealCode, setSocialRevealCode] = useState("");
  const [showSocial, setShowSocial] = useState(false);
  const [skillOptions, setSkillOptions] = useState(DEFAULT_STAFF_SKILLS);
  const [newSkillName, setNewSkillName] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [newStaffFiles, setNewStaffFiles] = useState([]);
  const [editedStaffFiles, setEditedStaffFiles] = useState([]);
  const [savingNew, setSavingNew] = useState(false);
  const storage = getStorage();

  const getStaffSortValue = (member, key) => {
    switch (key) {
      case "name":
        return `${member.firstName || ""} ${member.lastName || ""}`.trim();
      case "role":
        return member.role || "";
      case "skills": {
        const selected = Array.isArray(member.skills) ? member.skills : [];
        const other = member.otherSkills || "";
        return [...selected, other].filter(Boolean).join(", ");
      }
      case "position":
        return member.position || "";
      case "status":
        return member.status || "";
      case "workerType":
        return member.workerType || "Employee";
      default:
        return "";
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedStaff = useMemo(() => {
    const direction = sortConfig.direction === "asc" ? 1 : -1;

    const visibleStaff = staff.filter((member) => {
      const isDeactivated =
        member.isActive === false ||
        String(member.status || "").toLowerCase() === "deactivated" ||
        String(member.status || "").toLowerCase() === "inactive";

      return showDeactivated ? isDeactivated : !isDeactivated;
    });

    return [...visibleStaff].sort((a, b) => {
      const aValue = String(getStaffSortValue(a, sortConfig.key)).toLowerCase();
      const bValue = String(getStaffSortValue(b, sortConfig.key)).toLowerCase();

      return (
        aValue.localeCompare(bValue, undefined, {
          numeric: true,
          sensitivity: "base",
        }) * direction
      );
    });
  }, [staff, sortConfig, showDeactivated]);

  const sortIndicator = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  const sortableHeaderStyle = {
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
  };

  const uploadStaffFiles = async (staffId, files = []) => {
    const uploaded = [];
    for (const file of Array.from(files || [])) {
      const safeName = `${Date.now()}-${file.name}`.replace(/[^a-zA-Z0-9._-]/g, "_");
      const storageRef = ref(storage, `staff/${staffId}/${safeName}`);
      await uploadBytes(storageRef, file);
      uploaded.push({
        name: file.name,
        url: await getDownloadURL(storageRef),
        type: file.type || "",
        size: file.size || 0,
        uploadedAt: new Date().toISOString(),
      });
    }
    return uploaded;
  };

  const buildSharedContractorData = (record, staffId) => ({
    staffId,
    linkedToStaff: true,
    workerType: "Sub Contractor",
    firstName: record.firstName || "",
    lastName: record.lastName || "",
    contactName: `${record.firstName || ""} ${record.lastName || ""}`.trim(),
    email: record.email || "",
    companyName: record.companyName || "",
    coi: record.coi || {},
    phone: record.phone || "",
    ein: record.ein || "",
    address: record.address || "",
    city: record.city || "",
    state: record.state || "",
    zipCode: record.zip || "",
    skills: Array.isArray(record.skills) ? record.skills : [],
    otherSkills: record.otherSkills || "",
    status:
      record.isActive === false ||
      String(record.status || "").toLowerCase() === "deactivated"
        ? "Inactive"
        : "Active",
    updatedAt: new Date().toISOString(),
  });

  const syncSubContractorToContractors = async (record, staffId) => {
    if (record.workerType !== "Sub Contractor") return;

    const snap = await getDocs(collection(db, "contractors"));
    const match = snap.docs.find((item) => {
      const data = item.data();
      return (
        data.staffId === staffId ||
        (!data.staffId &&
          record.email &&
          String(data.email || "").toLowerCase() === String(record.email).toLowerCase())
      );
    });

    const shared = buildSharedContractorData(record, staffId);

    if (match) {
      await updateDoc(doc(db, "contractors", match.id), shared);
    } else {
      await addDoc(collection(db, "contractors"), {
        ...shared,
        companyName: record.companyName || "",
        industry: "",
        insuranceCertificates: "No",
        generalLiability: "",
        commercialAuto: "",
        workersComp: "",
        generalLiabilityPolicy: "",
        commercialAutoPolicy: "",
        workersCompPolicy: "",
        contractorLicenseNumber: "",
        contractorLicenseType: "",
        contractorLicenseState: "",
        contractorLicenseExpiration: "",
        notes: "",
        complianceFiles: [],
        createdAt: new Date().toISOString(),
      });
    }
  };

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const staffList = querySnapshot.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            ...data,
            skills: Array.isArray(data.skills) ? data.skills : [],
            companyName: data.companyName || "",
            coi: data.coi || emptyCoi(),
          };
        });
        setStaff(staffList);
      } catch (error) {
        console.error("Error fetching staff:", error);
      }
    };

    const fetchSkillOptions = async () => {
      try {
        const snap = await getDocs(collection(db, "staffSkills"));
        const savedSkills = snap.docs.map((d) => d.data()?.name || d.id);
        setSkillOptions(uniqueSkills([...DEFAULT_STAFF_SKILLS, ...savedSkills]));
      } catch (error) {
        console.error("Error fetching staff skills:", error);
        setSkillOptions(DEFAULT_STAFF_SKILLS);
      }
    };

    fetchStaff();
    fetchSkillOptions();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setNewStaff((prev) => {
      const updatedStaff = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // "salary" stays the Firestore hourly-rate field; the field being typed in
      // drives the paired calculation.
      if (name === "salary") {
        updatedStaff.yearlySalary = calculateYearlyFromHourly(value);
      } else if (name === "yearlySalary") {
        updatedStaff.salary = calculateHourlyFromYearly(value);
      }

      if (name === "terminationDate") {
        updatedStaff.status = value ? "Inactive" : "Active";
      }

      return updatedStaff;
    });
  };

  const toggleSkillForNewStaff = (skill) => {
    setNewStaff((prev) => {
      const current = Array.isArray(prev.skills) ? prev.skills : [];
      const next = current.includes(skill)
        ? current.filter((item) => item !== skill)
        : [...current, skill];
      return { ...prev, skills: uniqueSkills(next) };
    });
  };

  const toggleSkillForEditedStaff = (skill) => {
    setEditedStaff((prev) => {
      const current = Array.isArray(prev.skills) ? prev.skills : [];
      const next = current.includes(skill)
        ? current.filter((item) => item !== skill)
        : [...current, skill];
      return { ...prev, skills: uniqueSkills(next) };
    });
  };

  const addSkillOption = async () => {
    const cleanName = normalizeSkillName(newSkillName);
    if (!cleanName) return;

    if (skillOptions.some((skill) => skill.toLowerCase() === cleanName.toLowerCase())) {
      alert("That skill is already in the dropdown.");
      setNewSkillName("");
      return;
    }

    try {
      await addDoc(collection(db, "staffSkills"), {
        name: cleanName,
        createdAt: new Date().toISOString(),
      });
      setSkillOptions((prev) => uniqueSkills([...prev, cleanName]));
      setNewSkillName("");
    } catch (error) {
      console.error("Error adding skill:", error);
      alert("Unable to add skill.");
    }
  };

  const renderSkillCheckboxes = (selectedSkills = [], onToggle) => (
    <div className="staff-skill-grid">
      {skillOptions.map((skill) => (
        <label key={skill} className="staff-check-label">
          <input
            type="checkbox"
            checked={(selectedSkills || []).includes(skill)}
            onChange={() => onToggle(skill)}
          />
          {skill}
        </label>
      ))}
    </div>
  );

  const renderOtherSkillsInput = (staffRecord = {}, onChange) => {
    const selected = Array.isArray(staffRecord.skills) ? staffRecord.skills : [];
    if (!selected.includes("Other")) return null;

    return (
      <div style={{ marginBottom: 10 }}>
        <label>Other Skill(s):</label>
        <input
          type="text"
          name="otherSkills"
          value={staffRecord.otherSkills || ""}
          onChange={onChange}
          placeholder="Example: Fumigation License, Wildlife Trapping"
        />
        <small className="staff-hint">Separate multiple skills with commas.</small>
      </div>
    );
  };

  const updateCoi = (setter, key, field, value) => {
    setter((prev) => ({
      ...prev,
      coi: {
        ...(prev.coi || {}),
        [key]: {
          selected: false,
          insurerName: "",
          policyNumber: "",
          expirationDate: "",
          ...((prev.coi || {})[key] || {}),
          [field]: value,
        },
      },
    }));
  };

  const renderSubContractorFields = (record, setter) => {
    if (record.workerType !== "Sub Contractor") return null;

    return (
      <div className="staff-sub-section">
        <h3>Sub Contractor Information</h3>
        <div className="staff-form-group">
          <label>Company Name:</label>
          <input
            type="text"
            value={record.companyName || ""}
            onChange={(e) => setter((prev) => ({ ...prev, companyName: e.target.value }))}
            placeholder="Enter company name"
          />
        </div>

        <div className="staff-coi-popup">
          <h4>Certificate of Insurance (COI)</h4>
          <p className="staff-coi-help">Select all insurance types carried by this subcontractor.</p>
          <div className="staff-coi-choice-list">
            {COI_OPTIONS.map(([key, label]) => {
              const item = (record.coi || {})[key] || {};
              return (
                <div className={`staff-coi-option ${item.selected ? "selected" : ""}`} key={key}>
                  <label className="staff-coi-checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(item.selected)}
                      onChange={(e) => updateCoi(setter, key, "selected", e.target.checked)}
                    />
                    {label}
                  </label>
                  {item.selected && (
                    <div className="staff-coi-fields">
                      <div className="staff-form-group">
                        <label>Insurer Name:</label>
                        <input
                          type="text"
                          value={item.insurerName || ""}
                          onChange={(e) => updateCoi(setter, key, "insurerName", e.target.value)}
                        />
                      </div>
                      <div className="staff-form-group">
                        <label>Policy Number:</label>
                        <input
                          type="text"
                          value={item.policyNumber || ""}
                          onChange={(e) => updateCoi(setter, key, "policyNumber", e.target.value)}
                        />
                      </div>
                      <div className="staff-form-group">
                        <label>Expiration Date:</label>
                        <input
                          type="date"
                          value={item.expirationDate || ""}
                          onChange={(e) => updateCoi(setter, key, "expirationDate", e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderStateSelect = (value, onChange) => (
    <select name="state" value={value || ""} onChange={onChange}>
      <option value="">Select State</option>
      {US_STATES.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );

  const renderRoleSelect = (value, onChange) => (
    <select name="role" value={value || ""} onChange={onChange}>
      {ROLE_OPTIONS.map(([val, label]) => (
        <option key={val} value={val}>
          {label}
        </option>
      ))}
    </select>
  );

  const renderPayMethodSelect = (value, onChange) => (
    <select name="payCalculationMethod" value={value || "hourly_only"} onChange={onChange}>
      {Object.entries(PAY_METHOD_LABELS).map(([val, label]) => (
        <option key={val} value={val}>
          {label}
        </option>
      ))}
    </select>
  );

  const renderSocialReveal = () => (
    <div className="staff-social-reveal">
      <input
        type="password"
        placeholder="Enter code to show social"
        value={socialRevealCode}
        onChange={(e) => setSocialRevealCode(e.target.value)}
      />
      <button
        type="button"
        className="blue-button"
        onClick={() => {
          if (socialRevealCode === "0417") {
            setShowSocial(true);
          } else {
            alert("Incorrect code.");
            setShowSocial(false);
          }
        }}
      >
        Show Social
      </button>
      <button
        type="button"
        className="rojo-button"
        onClick={() => {
          setShowSocial(false);
          setSocialRevealCode("");
        }}
      >
        Hide
      </button>
    </div>
  );

  const renderWorkerTypeRadios = (value, onChange) => (
    <div className="staff-radio-row">
      <label className="staff-check-label">
        <input
          type="radio"
          name="workerType"
          value="Employee"
          checked={(value || "Employee") === "Employee"}
          onChange={onChange}
        />
        Employee
      </label>
      <label className="staff-check-label">
        <input
          type="radio"
          name="workerType"
          value="Sub Contractor"
          checked={value === "Sub Contractor"}
          onChange={onChange}
        />
        Sub Contractor
      </label>
    </div>
  );

  const handleNewStaffFileSelection = (e) => {
    setNewStaffFiles(Array.from(e.target.files || []));
  };

  const handleEditedStaffFileSelection = (e) => {
    setEditedStaffFiles(Array.from(e.target.files || []));
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    if (savingNew) return;
    setSavingNew(true);

    try {
      const creatorAuth = getStaffCreatorAuth();
      const userCredential = await createUserWithEmailAndPassword(
        creatorAuth,
        newStaff.email,
        newStaff.password
      );
      const uid = userCredential.user.uid;
      try {
        await signOut(creatorAuth);
      } catch (signOutErr) {
        console.warn("Staff creator sign-out failed:", signOutErr);
      }

      const uploadedFiles = await uploadStaffFiles(uid, newStaffFiles);

      // Never store the login password on the staff record.
      const { password: _omitPassword, ...staffFields } = newStaff;

      // Seed every Staff Settings flag explicitly so a new record never relies
      // on an implicit default. Values match STAFF_SETTINGS_DEFAULTS.
      const payload = applyStaffSettingsToRecord(
        {
          ...staffFields,
          files: uploadedFiles,
          authUid: uid,
          notificationPrefs: {
            jobs: false,
            proposals: false,
          },
        },
        {
          hideFromTeamMessage: false,
          receiveVehicleExpirationEmails: false,
          receiveJobScheduleEmails: true,
          receiveJobUpdateEmails: true,
          showInDropdowns: true,
        }
      );

      await setDoc(doc(db, "users", uid), payload);
      await syncSubContractorToContractors(payload, uid);

      setStaff((prev) => [...prev, { id: uid, ...payload }]);
      setNewStaff(emptyNewStaff());
      setNewStaffFiles([]);
      setShowAddStaffPopup(false);
    } catch (error) {
      console.error("Error adding staff:", error);
      alert("Error: " + error.message);
    } finally {
      setSavingNew(false);
    }
  };

  const handleDetailsClick = (staffMember) => {
    setSelectedStaff(staffMember);
    setEditedStaff({ ...staffMember });
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedStaff({ ...selectedStaff });
  };

  const handleInputChangeForEdit = (e) => {
    const { name, value, type, checked } = e.target;

    setEditedStaff((prev) => {
      const updatedStaff = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "salary") {
        updatedStaff.yearlySalary = calculateYearlyFromHourly(value);
      } else if (name === "yearlySalary") {
        updatedStaff.salary = calculateHourlyFromYearly(value);
      }

      return updatedStaff;
    });
  };

  const handleSaveChanges = async (event) => {
    event.preventDefault();

    if (!editedStaff?.id) {
      alert("Error: Staff ID is missing. Cannot update.");
      return;
    }

    try {
      const existingFiles = Array.isArray(editedStaff.files) ? editedStaff.files : [];
      const uploadedFiles = await uploadStaffFiles(editedStaff.id, editedStaffFiles);
      const updatedData = {
        ...editedStaff,
        files: [...existingFiles, ...uploadedFiles],
      };

      delete updatedData.id;

      await updateDoc(doc(db, "users", editedStaff.id), updatedData);
      await syncSubContractorToContractors(updatedData, editedStaff.id);

      setStaff((prev) =>
        prev.map((member) =>
          member.id === editedStaff.id ? { ...member, ...updatedData } : member
        )
      );

      const nextSelected = { ...selectedStaff, ...updatedData, id: editedStaff.id };
      setSelectedStaff(nextSelected);
      setEditedStaff(nextSelected);
      setEditedStaffFiles([]);
      setIsEditing(false);
      alert("Staff member updated successfully!");
    } catch (error) {
      console.error("Error updating staff member:", error);
      alert("Error: Unable to save changes.");
    }
  };

  const handleDeactivateStaff = async (member) => {
    if (!member?.id) return;

    const confirmed = window.confirm(
      `Deactivate ${member.firstName || ""} ${member.lastName || ""}?\n\n` +
        "They will be hidden from the active Staff list. Historical job assignments and pricing are not deleted."
    );
    if (!confirmed) return;

    try {
      const deactivatedAt = new Date().toISOString();

      await updateDoc(doc(db, "users", member.id), {
        status: "Deactivated",
        isActive: false,
        deactivatedAt,
      });

      setStaff((prevStaff) =>
        prevStaff.map((item) =>
          item.id === member.id
            ? { ...item, status: "Deactivated", isActive: false, deactivatedAt }
            : item
        )
      );

      if (selectedStaff?.id === member.id) {
        setSelectedStaff(null);
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Error deactivating staff:", error);
      alert("Unable to deactivate this staff member.");
    }
  };

  const handleReactivateStaff = async (member) => {
    if (!member?.id) return;

    try {
      const reactivatedAt = new Date().toISOString();

      await updateDoc(doc(db, "users", member.id), {
        status: "Active",
        isActive: true,
        reactivatedAt,
      });

      setStaff((prevStaff) =>
        prevStaff.map((item) =>
          item.id === member.id
            ? { ...item, status: "Active", isActive: true, reactivatedAt }
            : item
        )
      );
    } catch (error) {
      console.error("Error reactivating staff:", error);
      alert("Unable to reactivate this staff member.");
    }
  };

  const handleDeleteStaffFile = async (fileToDelete, fileIndex) => {
    if (!selectedStaff?.id) return;

    const confirmed = window.confirm(
      `Delete ${fileToDelete?.name || `File ${fileIndex + 1}`}?`
    );
    if (!confirmed) return;

    try {
      const currentFiles = Array.isArray(selectedStaff.files) ? selectedStaff.files : [];

      if (fileToDelete?.url) {
        try {
          await deleteObject(ref(storage, fileToDelete.url));
        } catch (storageError) {
          if (storageError?.code !== "storage/object-not-found") {
            throw storageError;
          }
        }
      }

      const nextFiles = currentFiles.filter((_, index) => index !== fileIndex);

      await updateDoc(doc(db, "users", selectedStaff.id), { files: nextFiles });

      setStaff((prev) =>
        prev.map((member) =>
          member.id === selectedStaff.id ? { ...member, files: nextFiles } : member
        )
      );

      setSelectedStaff((prev) => ({ ...prev, files: nextFiles }));
      setEditedStaff((prev) => (prev ? { ...prev, files: nextFiles } : prev));
    } catch (error) {
      console.error("Error deleting staff file:", error);
      alert("Unable to delete this file.");
    }
  };

  const handleDeleteStaff = async () => {
    try {
      await deleteDoc(doc(db, "users", selectedStaff.id));
      setStaff((prev) => prev.filter((member) => member.id !== selectedStaff.id));
      setSelectedStaff(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting staff:", error);
      alert("Unable to delete this staff member.");
    }
  };

  const closeModal = () => {
    setShowAddStaffPopup(false);
    setSelectedStaff(null);
    setIsEditing(false);
    setShowSocial(false);
    setSocialRevealCode("");
    setEditedStaffFiles([]);
  };

  return (
    <div className="staff-page">
      <h1>Staff Management</h1>

      <div className="staff-top-actions">
        <button className="blue-button" onClick={() => setShowAddStaffPopup(true)}>
          Click Here to Add Staff
        </button>

        <button
          type="button"
          className="styled-button"
          onClick={() => setShowDeactivated((prev) => !prev)}
        >
          {showDeactivated ? "Show Active Staff" : "Show Deactivated Staff"}
        </button>
      </div>

      <div className="staff-skill-card">
        <h3>Staff Skill Dropdown</h3>
        <p>Add new services or skills here. These will show in the staff skill checklist.</p>
        <div className="staff-skill-entry">
          <input
            type="text"
            name="staffSkillEntry"
            id="staff-skill-entry"
            value={newSkillName}
            onChange={(e) => setNewSkillName(e.target.value)}
            placeholder="Add skill, service, or certification"
            autoComplete="one-time-code"
            autoCorrect="off"
            autoCapitalize="words"
            spellCheck={false}
            data-lpignore="true"
            data-1p-ignore="true"
          />
          <button type="button" className="blue-button" onClick={addSkillOption}>
            Add Skill
          </button>
        </div>
      </div>

      {showAddStaffPopup && (
        <StaffPopup>
          <h3 className="staff-modal-title">Add User</h3>
          <form className="staff-form" onSubmit={handleAddStaff} autoComplete="off">
            <label>First Name:</label>
            <input type="text" name="firstName" value={newStaff.firstName} onChange={handleInputChange} required />
            <label>Last Name:</label>
            <input type="text" name="lastName" value={newStaff.lastName} onChange={handleInputChange} required />
            <label>Email:</label>
            <input type="email" name="email" value={newStaff.email} onChange={handleInputChange} required />
            <label>Password:</label>
            <input
              type="password"
              name="password"
              value={newStaff.password}
              onChange={handleInputChange}
              autoComplete="new-password"
              required
            />
            <label>Phone:</label>
            <input type="tel" name="phone" value={newStaff.phone} onChange={handleInputChange} />
            <label>Social Security Number:</label>
            <input
              type="password"
              name="social"
              value={newStaff.social}
              onChange={handleInputChange}
              placeholder="XXX-XX-XXXX"
              autoComplete="off"
            />
            <label>EIN:</label>
            <input type="text" name="ein" value={newStaff.ein} onChange={handleInputChange} placeholder="XX-XXXXXXX" />
            <label>Date of Birth:</label>
            <input type="date" name="dob" value={newStaff.dob} onChange={handleInputChange} />
            <label>Worker Type:</label>
            {renderWorkerTypeRadios(newStaff.workerType, handleInputChange)}
            {renderSubContractorFields(newStaff, setNewStaff)}
            <label>Address:</label>
            <input type="text" name="address" value={newStaff.address} onChange={handleInputChange} />
            <label>City:</label>
            <input type="text" name="city" value={newStaff.city} onChange={handleInputChange} />
            <label>State:</label>
            {renderStateSelect(newStaff.state, handleInputChange)}
            <label>Zip:</label>
            <input type="text" name="zip" value={newStaff.zip} onChange={handleInputChange} />
            <label>Position:</label>
            <input type="text" name="position" value={newStaff.position} onChange={handleInputChange} />
            <label>Skills / Services:</label>
            {renderSkillCheckboxes(newStaff.skills, toggleSkillForNewStaff)}
            {renderOtherSkillsInput(newStaff, handleInputChange)}
            <label>Images / Files:</label>
            <input
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onChange={handleNewStaffFileSelection}
            />
            {newStaffFiles.length > 0 && (
              <small className="staff-hint">{newStaffFiles.length} file(s) selected.</small>
            )}
            <label>Work Hourly Rate:</label>
            <input
              type="number"
              name="salary"
              min="0"
              step="0.01"
              value={newStaff.salary}
              onChange={handleInputChange}
              placeholder="Example: 25.00"
            />
            <label>Yearly Salary:</label>
            <input
              type="number"
              name="yearlySalary"
              min="0"
              step="0.01"
              value={newStaff.yearlySalary || ""}
              onChange={handleInputChange}
              placeholder="Example: 52000.00"
            />
            <small className="staff-hint">
              Enter either amount. The other is calculated automatically using 2,080 work hours per year.
            </small>
            <label>Paid Hours Per Day:</label>
            <input
              type="number"
              name="hoursPerDay"
              min="0"
              step="0.25"
              value={newStaff.hoursPerDay ?? ""}
              onChange={handleInputChange}
              placeholder="8"
            />
            <label>Flat Day Rate (optional):</label>
            <input
              type="number"
              name="dailyPayRate"
              min="0"
              step="0.01"
              value={newStaff.dailyPayRate || ""}
              onChange={handleInputChange}
              placeholder="Leave blank to use hourly rate × hours"
            />
            <small className="staff-hint">
              Tech Daily Pay = flat day rate if entered, otherwise Work Hourly Rate × hours worked
              (from Clock In / Out, or Paid Hours Per Day). Used by every Accounting page.
            </small>
            <label>Pay Calculation Method:</label>
            {renderPayMethodSelect(newStaff.payCalculationMethod, handleInputChange)}
            {newStaff.payCalculationMethod !== "hourly_only" && (
              <>
                <label>Default Job Percentage:</label>
                <input
                  type="number"
                  name="commissionRate"
                  min="0"
                  step="0.01"
                  value={newStaff.commissionRate || ""}
                  onChange={handleInputChange}
                  placeholder="Example: 10"
                />
                <small className="staff-hint">
                  Applies to assigned jobs unless that job has its own override.
                </small>
              </>
            )}
            <label>Start Date:</label>
            <input type="date" name="startDate" value={newStaff.startDate} onChange={handleInputChange} />
            <label>Termination Date:</label>
            <input type="date" name="terminationDate" value={newStaff.terminationDate} onChange={handleInputChange} />
            <label>Role:</label>
            {renderRoleSelect(newStaff.role, handleInputChange)}
            <div className="staff-modal-buttons">
              <button className="blue-button" type="submit" disabled={savingNew}>
                {savingNew ? "Saving..." : "Save"}
              </button>
              <button className="rojo-button" type="button" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </form>
        </StaffPopup>
      )}

      <h2>Users List</h2>
      <div className="staff-table-wrap">
        <table className="users-table staff-users-table">
          <thead>
            <tr>
              <th style={sortableHeaderStyle} onClick={() => handleSort("name")} title="Click to sort by name">
                Name{sortIndicator("name")}
              </th>
              <th style={sortableHeaderStyle} onClick={() => handleSort("role")} title="Click to sort by role">
                Role{sortIndicator("role")}
              </th>
              <th style={sortableHeaderStyle} onClick={() => handleSort("skills")} title="Click to sort by skills">
                Skills{sortIndicator("skills")}
              </th>
              <th style={sortableHeaderStyle} onClick={() => handleSort("position")} title="Click to sort by position">
                Position{sortIndicator("position")}
              </th>
              <th style={sortableHeaderStyle} onClick={() => handleSort("status")} title="Click to sort by status">
                Status{sortIndicator("status")}
              </th>
              <th
                style={sortableHeaderStyle}
                onClick={() => handleSort("workerType")}
                title="Click to sort by employee or sub contractor"
              >
                Employee / Sub Contractor{sortIndicator("workerType")}
              </th>
              <th title="Managed on the Staff Settings page">Settings</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!sortedStaff.length && (
              <tr>
                <td colSpan={8} className="staff-empty">
                  {showDeactivated ? "No deactivated staff." : "No active staff yet."}
                </td>
              </tr>
            )}
            {sortedStaff.map((member) => {
              const settings = resolveStaffSettings(member);
              return (
                <tr key={member.id}>
                  <td>{`${member.firstName || ""} ${member.lastName || ""}`.trim()}</td>
                  <td>{member.role}</td>
                  <td>
                    {[
                      ...(Array.isArray(member.skills) ? member.skills : []),
                      ...(member.otherSkills
                        ? String(member.otherSkills)
                            .split(",")
                            .map((skill) => skill.trim())
                            .filter(Boolean)
                        : []),
                    ].join(", ")}
                  </td>
                  <td>{member.position}</td>
                  <td>{member.status}</td>
                  <td>{member.workerType || "Employee"}</td>
                  <td>
                    <div className="staff-settings-badges">
                      {STAFF_SETTING_FIELDS.map((field) => {
                        const value = settings[field.key] === true;
                        return (
                          <span
                            key={field.key}
                            className={
                              value
                                ? "staff-settings-badge staff-settings-badge-yes"
                                : "staff-settings-badge staff-settings-badge-no"
                            }
                            title={`${field.label}: ${value ? "Yes" : "No"}`}
                          >
                            {field.shortLabel}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="staff-actions-cell">
                    <button className="styled-button" onClick={() => handleDetailsClick(member)}>
                      Details
                    </button>
                    {showDeactivated ? (
                      <button type="button" className="styled-button" onClick={() => handleReactivateStaff(member)}>
                        Reactivate
                      </button>
                    ) : (
                      <button type="button" className="styled-button" onClick={() => handleDeactivateStaff(member)}>
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedStaff && (
        <StaffPopup>
          <h2 className="staff-modal-title">Staff Details</h2>
          <div className="staff-popup-content">
            {isEditing ? (
              <form className="staff-form" onSubmit={handleSaveChanges} autoComplete="off">
                <label>First Name:</label>
                <input type="text" name="firstName" value={editedStaff.firstName || ""} onChange={handleInputChangeForEdit} required />
                <label>Last Name:</label>
                <input type="text" name="lastName" value={editedStaff.lastName || ""} onChange={handleInputChangeForEdit} required />
                <label>Email:</label>
                <input type="email" name="email" value={editedStaff.email || ""} onChange={handleInputChangeForEdit} required />
                <label>Phone:</label>
                <input type="tel" name="phone" value={editedStaff.phone || ""} onChange={handleInputChangeForEdit} />
                <label>Social Security Number:</label>
                <input
                  type={showSocial ? "text" : "password"}
                  name="social"
                  value={editedStaff.social || ""}
                  onChange={handleInputChangeForEdit}
                  placeholder="XXX-XX-XXXX"
                  autoComplete="off"
                />
                {renderSocialReveal()}
                <label>EIN:</label>
                <input type="text" name="ein" value={editedStaff.ein || ""} onChange={handleInputChangeForEdit} />
                <label>Date of Birth:</label>
                <input type="date" name="dob" value={editedStaff.dob || ""} onChange={handleInputChangeForEdit} />
                <label>Worker Type:</label>
                {renderWorkerTypeRadios(editedStaff.workerType, handleInputChangeForEdit)}
                {renderSubContractorFields(editedStaff, setEditedStaff)}
                <label>Address:</label>
                <input type="text" name="address" value={editedStaff.address || ""} onChange={handleInputChangeForEdit} />
                <label>City:</label>
                <input type="text" name="city" value={editedStaff.city || ""} onChange={handleInputChangeForEdit} />
                <label>State:</label>
                {renderStateSelect(editedStaff.state, handleInputChangeForEdit)}
                <label>Zip:</label>
                <input type="text" name="zip" value={editedStaff.zip || ""} onChange={handleInputChangeForEdit} />
                <label>Position:</label>
                <input type="text" name="position" value={editedStaff.position || ""} onChange={handleInputChangeForEdit} />
                <label>Skills / Services:</label>
                {renderSkillCheckboxes(editedStaff.skills || [], toggleSkillForEditedStaff)}
                {renderOtherSkillsInput(editedStaff, handleInputChangeForEdit)}
                <label>Add Images / Files:</label>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                  onChange={handleEditedStaffFileSelection}
                />
                {editedStaffFiles.length > 0 && (
                  <small className="staff-hint">{editedStaffFiles.length} new file(s) selected.</small>
                )}
                <label>Work Hourly Rate:</label>
                <input
                  type="number"
                  name="salary"
                  min="0"
                  step="0.01"
                  value={editedStaff.salary || ""}
                  onChange={handleInputChangeForEdit}
                />
                <label>Yearly Salary:</label>
                <input
                  type="number"
                  name="yearlySalary"
                  min="0"
                  step="0.01"
                  value={editedStaff.yearlySalary || ""}
                  onChange={handleInputChangeForEdit}
                />
                <small className="staff-hint">
                  Enter either amount. The other is calculated automatically using 2,080 work hours per year.
                </small>
                <label>Paid Hours Per Day:</label>
                <input
                  type="number"
                  name="hoursPerDay"
                  min="0"
                  step="0.25"
                  value={editedStaff.hoursPerDay ?? ""}
                  onChange={handleInputChangeForEdit}
                  placeholder="8"
                />
                <label>Flat Day Rate (optional):</label>
                <input
                  type="number"
                  name="dailyPayRate"
                  min="0"
                  step="0.01"
                  value={editedStaff.dailyPayRate || ""}
                  onChange={handleInputChangeForEdit}
                  placeholder="Leave blank to use hourly rate × hours"
                />
                <small className="staff-hint">
                  Tech Daily Pay = flat day rate if entered, otherwise Work Hourly Rate × hours worked.
                </small>
                <label>Pay Calculation Method:</label>
                {renderPayMethodSelect(editedStaff.payCalculationMethod, handleInputChangeForEdit)}
                {(editedStaff.payCalculationMethod || "hourly_only") !== "hourly_only" && (
                  <>
                    <label>Default Job Percentage:</label>
                    <input
                      type="number"
                      name="commissionRate"
                      min="0"
                      step="0.01"
                      value={editedStaff.commissionRate || ""}
                      onChange={handleInputChangeForEdit}
                      placeholder="Example: 10"
                    />
                    <small className="staff-hint">
                      Used on assigned jobs unless a specific job override is entered.
                    </small>
                  </>
                )}
                <label>Start Date:</label>
                <input type="date" name="startDate" value={editedStaff.startDate || ""} onChange={handleInputChangeForEdit} />
                <label>Termination Date:</label>
                <input type="date" name="terminationDate" value={editedStaff.terminationDate || ""} onChange={handleInputChangeForEdit} />
                <label>Role:</label>
                {renderRoleSelect(editedStaff.role, handleInputChangeForEdit)}

                {/* Same five flags the Staff Settings page writes and the Cloud
                    Functions read, so editing here or there has identical effect. */}
                <div className="staff-settings-block">
                  <div className="staff-settings-heading">Staff Settings</div>
                  <div className="staff-settings-hint">
                    These also appear on the Staff Settings page, where they can be changed for everyone at once.
                  </div>
                  {STAFF_SETTING_FIELDS.map((field) => {
                    const current = resolveStaffSettings(editedStaff)[field.key] === true;
                    return (
                      <div className="staff-settings-row" key={field.key}>
                        <label style={{ fontWeight: 800, marginTop: 10 }}>{field.label}?</label>
                        <select
                          name={field.key}
                          value={current ? "yes" : "no"}
                          onChange={(e) =>
                            setEditedStaff((prev) =>
                              applyStaffSettingsToRecord(prev, {
                                [field.key]: e.target.value === "yes",
                              })
                            )
                          }
                        >
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                        <small className="staff-hint">{current ? field.yesMeans : field.noMeans}</small>
                      </div>
                    );
                  })}
                </div>

                <div className="staff-modal-buttons">
                  <button className="other-button" type="submit">
                    Save
                  </button>
                  <button className="rojo-button" type="button" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <p><strong>Name:</strong> {`${selectedStaff.firstName || ""} ${selectedStaff.lastName || ""}`.trim()}</p>

                <div className="staff-settings-block">
                  <div className="staff-settings-heading">Staff Settings</div>
                  {STAFF_SETTING_FIELDS.map((field) => {
                    const value = resolveStaffSettings(selectedStaff)[field.key] === true;
                    return (
                      <p key={field.key} style={{ margin: "4px 0" }}>
                        <strong>{field.label}:</strong> {value ? "Yes" : "No"}
                      </p>
                    );
                  })}
                </div>

                <p><strong>Email:</strong> {selectedStaff.email}</p>
                <p><strong>Phone:</strong> {selectedStaff.phone}</p>
                <p>
                  <strong>Social:</strong>{" "}
                  {showSocial ? selectedStaff.social : maskSocial(selectedStaff.social)}
                </p>
                {renderSocialReveal()}
                <p><strong>EIN:</strong> {selectedStaff.ein}</p>
                <p><strong>Date of Birth:</strong> {selectedStaff.dob}</p>
                <p><strong>Worker Type:</strong> {selectedStaff.workerType || "Employee"}</p>
                {selectedStaff.workerType === "Sub Contractor" && (
                  <div className="staff-coi-details">
                    <p><strong>Company Name:</strong> {selectedStaff.companyName || "Not provided"}</p>
                    <strong>Certificate of Insurance:</strong>
                    {COI_OPTIONS.some(([key]) => selectedStaff.coi?.[key]?.selected) ? (
                      COI_OPTIONS.filter(([key]) => selectedStaff.coi?.[key]?.selected).map(([key, label]) => (
                        <div className="staff-coi-detail-card" key={key}>
                          <strong>{label}</strong>
                          <span>Insurer: {selectedStaff.coi[key].insurerName || "Not provided"}</span>
                          <span>Policy #: {selectedStaff.coi[key].policyNumber || "Not provided"}</span>
                          <span>Expiration: {selectedStaff.coi[key].expirationDate || "Not provided"}</span>
                        </div>
                      ))
                    ) : (
                      <span> None selected</span>
                    )}
                  </div>
                )}
                <p><strong>Position:</strong> {selectedStaff.position}</p>
                <p>
                  <strong>Skills:</strong>{" "}
                  {Array.isArray(selectedStaff.skills) && selectedStaff.skills.length
                    ? selectedStaff.skills.join(", ")
                    : "None selected"}
                </p>
                {selectedStaff.otherSkills && (
                  <p><strong>Other Skill(s):</strong> {selectedStaff.otherSkills}</p>
                )}
                <p><strong>Work Hourly Rate:</strong> {selectedStaff.salary}</p>
                <p>
                  <strong>Tech Daily Pay:</strong>{" "}
                  {selectedStaff.dailyPayRate
                    ? `$${Number(selectedStaff.dailyPayRate).toFixed(2)} flat day rate`
                    : `${selectedStaff.hoursPerDay || 8} hrs × hourly rate`}
                </p>
                <p>
                  <strong>Yearly Salary:</strong>{" "}
                  {selectedStaff.yearlySalary ||
                    (selectedStaff.salary ? calculateYearlyFromHourly(selectedStaff.salary) : "")}
                </p>
                <p>
                  <strong>Pay Calculation Method:</strong>{" "}
                  {PAY_METHOD_LABELS[selectedStaff.payCalculationMethod || "hourly_only"]}
                </p>
                {(selectedStaff.payCalculationMethod || "hourly_only") !== "hourly_only" && (
                  <p><strong>Default Job Percentage:</strong> {selectedStaff.commissionRate || 0}%</p>
                )}
                <p><strong>Start Date:</strong> {selectedStaff.startDate}</p>
                <p><strong>Termination Date:</strong> {selectedStaff.terminationDate}</p>
                <p><strong>Address:</strong> {selectedStaff.address}</p>
                <p><strong>City:</strong> {selectedStaff.city}</p>
                <p><strong>State:</strong> {selectedStaff.state}</p>
                <p><strong>Zip:</strong> {selectedStaff.zip}</p>
                <p><strong>Role:</strong> {selectedStaff.role}</p>
                <div style={{ marginTop: 12 }}>
                  <strong>Images / Files:</strong>
                  {Array.isArray(selectedStaff.files) && selectedStaff.files.length ? (
                    <ul className="staff-file-list">
                      {selectedStaff.files.map((file, index) => (
                        <li key={`${file.url || file}-${index}`}>
                          <a href={file.url || file} target="_blank" rel="noreferrer">
                            {file.name || `File ${index + 1}`}
                          </a>
                          <button
                            type="button"
                            className="red-button staff-file-delete"
                            onClick={() => handleDeleteStaffFile(file, index)}
                          >
                            Delete File
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span> None</span>
                  )}
                </div>
                <div className="staff-modal-buttons">
                  <button className="blue-button" onClick={handleEditClick}>Edit</button>
                  <button className="red-button" onClick={() => setShowDeleteConfirm(true)}>Delete</button>
                  <button className="green-button" onClick={closeModal}>Close</button>
                </div>
              </>
            )}
          </div>
        </StaffPopup>
      )}

      {showDeleteConfirm && (
        <StaffPopup layer={1} width={420}>
          <h3 className="staff-modal-title">Are you sure you want to delete?</h3>
          <p style={{ color: "#475569", marginTop: 0 }}>
            This removes the staff record. Use Deactivate instead to keep history.
          </p>
          <div className="staff-modal-buttons">
            <button className="red-button" onClick={handleDeleteStaff}>Yes</button>
            <button className="green-button" onClick={() => setShowDeleteConfirm(false)}>No</button>
          </div>
        </StaffPopup>
      )}
    </div>
  );
};

export default Staff;
